import { getHeader, uploadAudioFileDetails } from './apiOptions';
import { fetchData, postDataV2 } from './apiService';
import { apiUrl, REACT_APP_WEBSOCKET_URL } from '../constants/constants';
import { getSpeakingTestFormattedDate, getUrlParams } from '../service/utils';
import { decryptURL } from '../service/DecryptionService';
import { AxiosResponse } from 'axios';
import { Probes } from '../interface/Probe';
import {
    InsperaObject,
    SpeedObject,
    settingsObject,
    TestTaker,
    LoadingTimes,
    PrecheckDetailsObject,
} from '../interface/interface';
import { Content } from '../interface/ContentType';

// Post data to the API using the postHeader function
export const uploadAudioFile = async (
    fileBlob: Blob,
    fileFormat: string,
    fileName: string,
    testTakerDetails: TestTaker,
    insperaDetails: InsperaObject,
    timestamps?: {
        recordingStartTimestamp?: string;
        recordingEndTimestamp?: string;
    },
) => {
    try {
        const data = uploadAudioFileDetails(
            fileBlob,
            fileFormat || '.mp3',
            fileName || '',
            testTakerDetails,
            insperaDetails,
            timestamps,
        );

        const response = await postDataV2(
            apiUrl.UPLOAD_AUDIO_FILE,
            data,
            { headers: { Authorization: 'Bearer ' + testTakerDetails.testTakerAccessToken } },
            'cmst',
        );

        return response;
    } catch (error: any) {
        let errorData;
        if (error.response) {
            errorData = error.response.data;
            console.error('Error fetching content data:', errorData.message);
            throw new Error(errorData.errorCode || error);
        }
        throw new Error('V112');
    }
};

export const notificationEventStart = async ({
    testTakerAccessToken,
    cmsSessionId,
}: {
    testTakerAccessToken: string;
    cmsSessionId: string;
}) => {
    try {
        const urlParams = getUrlParams();
        const response = await postDataV2(
            apiUrl.NOTIFICATION_EVENT_START,
            {
                cmsSessionId,
                assessmentRunId: urlParams.assessmentID,
                insperaUserId: urlParams.userID,
                compositeCandidateNumber: urlParams.externalId,
                externalTestTakerUuid: cmsSessionId,
                partnerCode: urlParams.partner,
            },
            { headers: { Authorization: 'Bearer ' + testTakerAccessToken } },
            'cmst',
        );
        return response;
    } catch (e) {
        console.error('Error in notification event start', e);
    }
};

export const notificationEventEnd = async ({
    testTakerDetails,
    insperaDetails,
    status,
    endDescription,
}: {
    testTakerDetails: TestTaker;
    insperaDetails: InsperaObject;
    status: 'SUCCESS' | 'FAILURE';
    endDescription: string;
}) => {
    try {
        const response = await postDataV2(
            apiUrl.NOTIFICATION_EVENT_END,
            {
                cmsSessionId: testTakerDetails.testTakerSessionId,
                assessmentRunId: insperaDetails.assessmentId,
                insperaUserId: testTakerDetails.testTakerId,
                compositeCandidateNumber: insperaDetails.externalId,
                externalTestTakerUuid: testTakerDetails.testTakerSessionId,
                partnerCode: insperaDetails.partner,
                endStatus: status,
                statusDescription: endDescription,
            },
            { headers: { Authorization: 'Bearer ' + testTakerDetails.testTakerAccessToken } },
            'cmst',
        );

        return response;
    } catch (e) {
        console.error('Error in notification event end', e);
    }
};

export const saveHistory = async ({
    testTakerDetails,
    productType,
    globalReferenceId,
    atcAllocationDetail,
}: {
    testTakerDetails: TestTaker;
    productType: string;
    globalReferenceId: string;
    atcAllocationDetail: Content['atcAllocationDetail'];
}) => {
    try {
        const response = await postDataV2(
            apiUrl.SAVE_HISTORY,
            {
                uttid: globalReferenceId,
                cmsSessionId: testTakerDetails.testTakerSessionId,
                frameAItem: atcAllocationDetail?.frameAItem,
                frameBItem: atcAllocationDetail?.frameBItem,
                frameCItem: atcAllocationDetail?.frameCItem,
                part23Item: atcAllocationDetail?.part23Item,
                productType: productType,
            },
            { headers: { Authorization: 'Bearer ' + testTakerDetails.testTakerAccessToken } },
            'atc',
        );

        return response;
    } catch (e) {
        console.error('Error in saving TT history', e);
    }
};

export const getContentData = async (
    testTakerDetails: TestTaker,
    productType: string,
    globalReferenceId?: string,
    calibrationStatus: string = 'DRAFT',
): Promise<AxiosResponse<Content>> => {
    try {
        const response = await postDataV2(
            apiUrl.GET_CONTENT_DATA,
            {
                testTakerId: testTakerDetails.testTakerId,
                languageCode: testTakerDetails.testTakerLanguage,
                cmsSessionId: testTakerDetails.testTakerSessionId,
                calibrationStatus: calibrationStatus,
                productType: productType,
                uttid: globalReferenceId,
                partsRequested: ['PART_1', 'PART_2', 'PART_3'],
            },
            { headers: { ...getHeader(), Authorization: 'Bearer ' + testTakerDetails.testTakerAccessToken } },
            'atc',
        );

        return response;
    } catch (error: any) {
        console.log(error);
        let errorData;
        if (error.response) {
            errorData = error.response.data;
            console.error('Error fetching content data:', errorData.message);
            throw new Error(errorData.errorCode || error);
        }
        throw new Error('V112');
    }
};

export const getLiteVersionContentData = async (
    testTakerDetails: TestTaker,
    productType: string,
    globalReferenceId?: string,
    calibrationStatus: string = 'DRAFT',
): Promise<AxiosResponse<Content>> => {
    try {
        const response = await postDataV2(
            apiUrl.GET_INT_SUITE_CONTENT,
            {
                testTakerId: testTakerDetails.testTakerId,
                languageCode: testTakerDetails.testTakerLanguage,
                cmsSessionId: testTakerDetails.testTakerSessionId,
                calibrationStatus: calibrationStatus,
                productType: productType,
                uttid: globalReferenceId,
                partsRequested: ['PART_1'],
            },
            { headers: { ...getHeader(), Authorization: 'Bearer ' + testTakerDetails?.testTakerAccessToken } },
            'atc',
        );

        return response;
    } catch (error: any) {
        let errorData;
        if (error.response) {
            errorData = error.response.data;
            console.error('Error fetching content data:', errorData.message);
            throw new Error(errorData.errorCode || error);
        }
        throw new Error('V112');
    }
};

export const getProbes = async (
    testTakerDetails: TestTaker,
    globalReferenceId: string,
    product: string = '',
    calibrationStatus: string = 'DRAFT',
) => {
    try {
        const response: AxiosResponse<Probes> = await postDataV2(
            apiUrl.GET_PROBES,
            {
                testTakerId: testTakerDetails.testTakerId,
                languageCode: testTakerDetails.testTakerLanguage,
                cmsSessionId: testTakerDetails.testTakerSessionId,
                calibrationStatus: calibrationStatus,
                productType: product,
                uttid: globalReferenceId,
            },
            { headers: { ...getHeader(), Authorization: 'Bearer ' + testTakerDetails.testTakerAccessToken } },
            'atc',
        );

        // Decrypt URLs and attach to the items
        const probesData = response.data;
        const probes = probesData.probes;

        for (const probe of probes) {
            probe.audioPreSignUrl = await decryptURL(probe.audioPreSignUrl);
        }

        return response;
    } catch (error: any) {
        let errorData;
        if (error.response) {
            errorData = error.response.data;
            console.error('Error fetching content data:', errorData.message);
            throw new Error(errorData.errorCode || error);
        }
        throw new Error('V112');
    }
};

export const connectToIntentAudioSocket = () => {
    let socket = new WebSocket(
        `${REACT_APP_WEBSOCKET_URL}${apiUrl.INTENT_AUDIO_DETECTION}`,
        // ["Authorization", testTaker?.testTakerAccessToken]
    );
    return socket;
};

export const saveSpeakingTestProgressionData = async ({
    testTakerDetails,
    playedAudioFilesDetails = [],
    status = 'Test Completed',
    fullRecordingFilePath = '',
    logId = '',
    insperaDetails = {
        assessmentId: '',
        externalId: '',
        partner: '',
        product: '',
    },
    speedDetails = { uploadSpeed: '', downloadSpeed: '' },
    settings = { fontSize: '', colourContrast: '' },
    loadingDurations = { loginLoadTime: 0, finalFileUploadingTime: 0, testLoadingTime: 0, audiofilesLoadingTime: 0 },
    precheckDetails = { selectedMicLabel: '', speakCheckDetails: { duration: 0, responseFilePath: '' } },
    partSummary = { PART_1: {}, PART_2: {}, PART_3: {} },
    disconnects = [],
}: {
    testTakerDetails: TestTaker;
    playedAudioFilesDetails: any[];
    status: string;
    fullRecordingFilePath: string;
    logId: string;
    insperaDetails: InsperaObject;
    speedDetails: SpeedObject;
    settings: settingsObject;
    loadingDurations: LoadingTimes;
    precheckDetails: PrecheckDetailsObject;
    partSummary: any;
    disconnects?: any[];
}) => {
    try {
        const response: AxiosResponse<any> = await postDataV2(
            apiUrl.SPEAKING_TEST_PROGRESSION_DATA,
            {
                id: logId,
                testTakerId: testTakerDetails?.testTakerId,
                cmsSessionId: testTakerDetails?.testTakerSessionId,
                centerName: testTakerDetails?.testTakerCenterName,
                testDate: getSpeakingTestFormattedDate().utc,
                testCentreTimeZone: getSpeakingTestFormattedDate().localTimezone,
                language: testTakerDetails?.testTakerLanguage,
                status: status,
                playedAudioFilesDetails: playedAudioFilesDetails,
                fullAudioRecordingFilePath: fullRecordingFilePath,
                ...insperaDetails,
                uploadSpeed: speedDetails?.uploadSpeed,
                downloadSpeed: speedDetails?.downloadSpeed,
                settings: settings,
                preCheck: {
                    speakCheckScreen: precheckDetails.speakCheckDetails,
                    selectedMicrophone: precheckDetails.selectedMicLabel,
                },
                loadingDurations: loadingDurations,
                partSummary: partSummary,
                disconnects,
            },
            { headers: { ...getHeader(), Authorization: 'Bearer ' + testTakerDetails?.testTakerAccessToken } },
            'cmst',
        );

        return response;
    } catch (error: any) {
        let errorData;
        if (error.response) {
            errorData = error.response.data;
            console.error('Error fetching content data:', errorData.message);
            throw new Error(errorData.errorCode || error);
        }
        throw new Error('V112');
    }
};

/**
 * @params takes a json object to send message to parent window
 */
export const sendMessageToInsperaIB = (payload: any) => {
    // Replace '*' with the iframe origin for better security.
    const finalPayload = JSON.parse(JSON.stringify(payload));
    window.parent.postMessage(finalPayload, `*`);
};
export const getCRELLAContent = async (testTakerDetails: TestTaker, examGroup: string, examSubGroup: string) => {
    try {
        const response: AxiosResponse<any> = await postDataV2(
            apiUrl.CRELLA_CONTENT,
            {
                testTakerId: testTakerDetails.testTakerId,
                centreName: testTakerDetails.testTakerCenterName,
                langAccent: testTakerDetails.testTakerLanguage,
                uniqueSessionId: testTakerDetails.testTakerSessionId,
                testTakerDate: getSpeakingTestFormattedDate().utc,
                examGroup: examGroup,
                examSubGroup: examSubGroup,
                productType: 'GT',
            },
            {
                headers: {
                    ...getHeader(),
                    Authorization: 'Bearer ' + testTakerDetails.testTakerAccessToken,
                },
            },
            'atc',
        );

        return response;
    } catch (error: any) {
        console.log('Failed fetching CRELLA content:', error);
        throw new Error(error);
    }
};

export const getCRELLAProbes = async (testTakerDetails: TestTaker, examGroup: string, examSubGroup: string) => {
    try {
        const response: AxiosResponse<any> = await postDataV2(
            apiUrl.CRELLA_PROBES,
            {
                testTakerId: testTakerDetails.testTakerId,
                centreName: testTakerDetails.testTakerCenterName,
                langAccent: testTakerDetails.testTakerLanguage,
                uniqueSessionId: testTakerDetails.testTakerSessionId,
                testTakerDate: getSpeakingTestFormattedDate().utc,
                examGroup: examGroup,
                examSubGroup: examSubGroup,
                productType: 'GT',
            },
            {
                headers: {
                    ...getHeader(),
                    Authorization: 'Bearer ' + testTakerDetails.testTakerAccessToken,
                },
            },
            'atc',
        );

        const {
            data: { Probes: probes },
        } = response;

        for (const probe of probes) {
            probe.audioPreSignUrl = await decryptURL(probe.audioPreSignUrl);
        }
        return response;
    } catch (error: any) {
        console.log('Failed fetching CRELLA probes:', error);
        throw new Error(error);
    }
};


export const getContentList = async (accessToken: string, pageNo: number = 1, rowCount: number = 9, taskId?: string) => {
    try {
        const url = taskId 
            ? `${apiUrl.PREVIEW_CONTENT}?taskId=${taskId}&pageNo=${pageNo}&rowCount=${rowCount}`
            : `${apiUrl.PREVIEW_CONTENT}?pageNo=${pageNo}&rowCount=${rowCount}`;
        
        const response: AxiosResponse<any> = await fetchData(url, { headers: { ...getHeader(), Authorization: `Bearer ${accessToken}` } }, 'atc' );
        return response.data;
    } catch (error: any) {
        console.log('Error fetching QA preview content:', error);
        throw new Error(error);
    }
};

export const getPreviewContentById = async (testTakerDetails: TestTaker, taskId: string) => {
    try {
        const response: AxiosResponse<any> = await fetchData(
            `${apiUrl.PREVIEW_CONTENT_BY_ID}?testTakerId=${testTakerDetails.testTakerId}&taskId=${taskId}&languageCode=${testTakerDetails.testTakerLanguage}`,
            { headers: { ...getHeader(), Authorization: `Bearer ${testTakerDetails.testTakerAccessToken}` } },
            'atc',
        );
        return response;
    } catch (error: any) {
        console.log('Error fetching preview content by ID:', error);
        throw new Error(error);
    }
};

export const getRating = () => {
    /* This part will be replaced once we integrated LEX. Till that we have to fetch Medium type
    in Part 3 as per story 859. Once lex is integarted, score will be generated through api. It will return 
    value between 0-10 .  if we receive less than 5,  medium will be played . If it is greater than or equal to
    5 , we will play  hard type question in  part 3. As of now to play medium hardcoded as 4*/
    const ratingScore = 6;
    return ratingScore;
}